import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CheckPointValueType, IssueCategoryType } from '../../../../common/enums/enums';
import { Localization } from '../../../../common/enums/localization';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { ChecklistsService } from '../../../../common/services/checklists.service';
import { AppConfig } from '../../../../../config/app.config';
import { TranslateService } from '@ngx-translate/core';
import { IdAndLabel } from '../../../../common/model/id-and-label';
import { TemplateCheckpoint } from '../../../../common/model/template-checkpoint';
import { flatMap, filter, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-add-custom-checkpoint-modal',
  templateUrl: './add-custom-checkpoint-modal.component.html',
})
export class AddCustomCheckpointModalComponent {

  @ViewChild('modal') modal: ModalDirective;
  @Output() changed: EventEmitter<any> = new EventEmitter<any>();

  public checkpointFormGroup: UntypedFormGroup;
  public parentNodeId: number;
  public buildingId: number;
  public isCreatingInProgress: boolean;
  public valueTypes: IdAndLabel[];
  public categories: IdAndLabel[];
  public readonly Localization = Localization;

  public get labelFormControl(): UntypedFormControl { return this.checkpointFormGroup.get('label') as UntypedFormControl; }
  public get valueTypeFormControl(): UntypedFormControl { return this.checkpointFormGroup.get('valueType') as UntypedFormControl; }
  public get categoryFormControl(): UntypedFormControl { return this.checkpointFormGroup.get('categoryType') as UntypedFormControl; }

  constructor(
    private checklistsService: ChecklistsService,
    private translate: TranslateService,
    public appConfig: AppConfig,
    ) {
  }

  public show(buildingId: number, parentNodeId: number, label: string) {
    this.parentNodeId = parentNodeId;
    this.buildingId = buildingId;
    this.initData();
    this.initForms();
    this.reset(label);
    this.modal.show();
  }

  public close() {
    this.modal.hide();
  }

  public onClosed() {
    this.checkpointFormGroup = null;
    this.parentNodeId = null;
    this.buildingId = null;
  }

  public initData() {
    this.valueTypes = Object.values(CheckPointValueType).map(x => {
      const translatedLabel = this.translate.instant(`checkPointValueType_${x}`);
      return new IdAndLabel(x, translatedLabel);
    });
    this.categories = Object.values(IssueCategoryType).map(x => {
      const translatedLabel = this.translate.instant(`issueCategoryType_${x}`);
      return new IdAndLabel(x, translatedLabel);
    });
  }

  public initForms() {
    this.checkpointFormGroup = new UntypedFormGroup({
      'label': new UntypedFormControl('', [Validators.required]),
      'valueType': new UntypedFormControl(null, [Validators.required]),
      'categoryType': new UntypedFormControl(null, [Validators.required])
    });
  }

  private markAsTouched(formGroup: UntypedFormGroup) {
    Object.values(formGroup.controls)
      .forEach(c => c.markAsTouched());
  }

  public onCheckpointSubmit() {
    if (this.checkpointFormGroup.invalid) {
      this.markAsTouched(this.checkpointFormGroup);
    } else {
      this.createCustomCheckpoint();
    }
  }

  private createCustomCheckpoint() {
    this.isCreatingInProgress = true;
    const newCustomCheckpoint: TemplateCheckpoint = {
      ...this.checkpointFormGroup.value,
      reference: this.checkpointFormGroup.value.label,
    };

    this.checklistsService.addNewCheckpoint(newCustomCheckpoint)
      .pipe(
        filter(templateCheckpoint => templateCheckpoint.id !== undefined),
        flatMap(templateCheckpoint => this.checklistsService.addCheckpoint(this.parentNodeId, templateCheckpoint.id))
      )
      .subscribe({
        complete: () => {
          this.isCreatingInProgress = false;
          this.changed.emit(null);
          this.close();
        }
      });
  }

  private reset(label: string) {
    this.checkpointFormGroup.reset({
      label,
    });
  }
}
